import { Link } from "react-router-dom";
import { Section, Eyebrow, DisclaimerNote } from "@/components/Primitives";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

const startHere = [
  {
    icon: "fa-compass",
    label: "Not sure what you need?",
    title: "TENET Transformation Readiness Assessment",
    body: "A gentle, private snapshot of where you are and what kind of support may help.",
    to: "/assessments/tenet-readiness",
    cta: "Start here",
    testid: "faq-start-readiness",
  },
  {
    icon: "fa-fire",
    label: "Unwanted pornography or sexual behavior?",
    title: "Sacred Flame Readiness Assessment",
    body: "TENET's specialized starting point for this specific, deeply personal work.",
    to: "/assessments/sacred-flame-readiness",
    cta: "Start here",
    testid: "faq-start-sacred-flame",
  },
  {
    icon: "fa-book-open",
    label: "Prefer to learn privately first?",
    title: "The Sacred Flame Guidebook",
    body: "Begin with the free guide, Sex Transmutation, before taking any assessment.",
    to: "/book",
    cta: "Get the free guide",
    testid: "faq-start-guidebook",
  },
];

const faqSections = [
  {
    heading: "Getting started",
    items: [
      {
        q: "What should I expect during my first appointment?",
        a: [
          "In your first session, we'll talk about your goals, personal history, and the emotional or behavioral patterns you want to change. You'll get a chance to ask any questions, and I'll explain the hypnotherapy process so you feel comfortable.",
          "Then, we'll move into the actual work\u2014usually a gentle induction into trance, followed by subconscious exploration and emotional reframing. Most clients leave the first session feeling relaxed, hopeful, and more aware of what's possible.",
        ],
      },
      {
        q: "What does a session feel like?",
        a: [
          "A session typically begins with conversation about your goals, recent experiences, or emotional patterns. From there, you'll be gently guided into a calm, focused state of relaxation\u2014not sleep, but a state where your body feels relaxed and your mind feels open.",
          "Once in trance, we explore subconscious thoughts, visualizations, memories, or inner parts that are ready to shift. You'll remain aware and in control, but also deeply immersed in the experience. Most clients describe it as peaceful, insightful, and emotionally freeing.",
          "Afterward, people often report feeling lighter, clearer, more grounded, or spiritually connected.",
        ],
      },
      {
        q: "Can I do this from home?",
        a: [
          "Yes\u2014hypnotherapy works extremely well over Zoom. Many clients meet virtually, often from the comfort of their bedroom or home office. All you need is a quiet, private space, a reliable internet connection, and a willingness to engage in the process.",
          "Virtual hypnotherapy allows for the same depth of work\u2014and in some cases, even deeper comfort, since you're in your own familiar environment. Whether you're across town or across the country, we can do meaningful work together remotely.",
        ],
      },
      {
        q: "How many sessions will I need?",
        a: [
          "The number of sessions depends on your goals, emotional history, and how deeply rooted your issue is. Some clients experience major breakthroughs in just 1\u20133 sessions, while others benefit from a full 6\u201310 session plan. For ongoing personal growth or healing from complex trauma, longer support may be helpful.",
          "We'll work together to assess your progress regularly and adapt the plan to your needs. The goal is always to make each session count and empower you to move forward with clarity, confidence, and healing.",
        ],
      },
    ],
  },
  {
    heading: "Safety & control",
    items: [
      {
        q: "Is hypnotherapy safe?",
        a: [
          "Yes\u2014hypnotherapy is a safe and natural process. It is simply a state of focused relaxation that allows the subconscious mind to become more open to positive suggestions, emotional resolution, and healing insight.",
          "You are not unconscious or asleep during hypnotherapy. Instead, you enter a calm, receptive state\u2014similar to meditation or deep prayer\u2014where your mind can gently process emotions and shift limiting beliefs. You remain in control the entire time and can choose to come out of the trance state at any moment.",
          "Rather than working against your body, hypnotherapy works with your nervous system\u2014helping you find healing at your own pace.",
        ],
      },
      {
        q: "Will I lose control during hypnosis?",
        a: [
          "No. This is one of the biggest misconceptions about hypnosis. You do not lose control during hypnotherapy.",
          "In fact, the opposite is true\u2014hypnotherapy helps you gain more control over areas of your life that previously felt automatic or overwhelming. You'll remain fully aware of what's happening in the session and can talk, move, or stop at any time.",
          "Clinical hypnotherapy is not the same as stage hypnosis. It's a respectful, guided process that helps quiet your overactive, analytical mind so you can connect more directly with your inner truth, emotions, and strengths. You are always the one in charge.",
        ],
      },
      {
        q: "What if I can't be hypnotized?",
        a: [
          "It's a common fear, but in reality, most people can experience hypnosis\u2014as long as they're willing to participate in the process. Hypnosis is not about giving up control; it's about entering a focused, receptive state. Some people enter a light trance, others go deeper, but either level can support significant healing and change.",
          "Each session is tailored to match your natural strengths and personality, whether you're highly analytical, creative, skeptical, or deeply intuitive. With the right approach, nearly anyone can experience the benefits of hypnotherapy.",
        ],
      },
      {
        q: "What if I get emotional or cry during a session?",
        a: [
          "That's completely normal\u2014and often part of the healing process. Emotions that surface during hypnotherapy are your mind's way of letting go of pain that's been buried or avoided.",
          "You'll be in a safe space where you're free to feel without judgment. We'll work through the emotions gently, helping you move through them rather than getting stuck in them.",
        ],
      },
    ],
  },
  {
    heading: "How it works",
    items: [
      {
        q: "How is this different from talk therapy?",
        a: [
          "While talk therapy helps you understand your problems and develop conscious strategies, hypnotherapy works at the deeper level of the subconscious mind\u2014where many root issues actually reside. In hypnotherapy, we gently move past the critical filter of the conscious mind and access the beliefs, emotions, and past experiences that influence your behavior and emotional responses.",
          "This means that instead of only talking about the problem, we're shifting emotional energy, transforming belief systems, and helping you experience change from the inside out. Many clients find that what took years to discuss in traditional therapy can begin resolving in just a few focused hypnotherapy sessions.",
        ],
      },
      {
        q: "What kinds of issues can hypnotherapy help with?",
        a: [
          "Hypnotherapy can be helpful for a wide variety of emotional, behavioral, and spiritual challenges. It's especially useful when you're ready to go beyond surface-level coping and explore the root causes of your distress.",
        ],
        list: [
          "Pornography use and compulsive behaviors",
          "Anxiety, panic, and chronic stress",
          "Trauma, PTSD, and emotional overwhelm",
          "Grief and loss that feels unresolved or stuck",
          "Spiritual disconnection or identity confusion",
          "Self-worth struggles and inner critical voices",
          "Weight concerns, emotional eating, or body image pain",
        ],
        after: [
          "Because hypnotherapy works directly with the subconscious mind, it can support meaningful breakthroughs\u2014often in fewer sessions than traditional talk therapy.",
        ],
      },
    ],
  },
  {
    heading: "Trauma & relapse",
    items: [
      {
        q: "How is trauma handled in hypnotherapy?",
        a: [
          "Trauma is handled with great care and respect in hypnotherapy. This approach is designed to avoid retraumatization while still allowing the mind and body to process and release emotional pain. Rather than asking you to relive or re-experience traumatic events, you'll be guided into a safe, relaxed state where the subconscious mind can gently reframe how your body and mind store that experience.",
          "Techniques like the Rewind Method, parts therapy, and visualization allow the brain to reprocess trauma without being overwhelmed by it. Clients often report significant emotional relief, a sense of closure, and the ability to move forward without being triggered or weighed down by the past.",
        ],
      },
      {
        q: "Can this work if I don't remember my trauma?",
        a: [
          "Yes. You don't need to consciously remember a traumatic event for healing to take place. Hypnotherapy works with the subconscious mind, which stores emotional memories even when the conscious mind has forgotten or suppressed them.",
          "I use gentle, non-invasive techniques like parts therapy and symbolic processing to help release stored pain and shift subconscious patterns\u2014without needing to relive or recall every detail.",
        ],
      },
      {
        q: "What if I've relapsed many times before\u2014can this still work for me?",
        a: [
          "Many people come after trying multiple methods to quit, often feeling stuck in cycles of relapse and shame. Hypnotherapy doesn't just focus on behavior\u2014it explores the emotional roots, internal triggers, and subconscious beliefs driving the habit.",
          "This deeper level of work supports more lasting change, even if you've struggled for years.",
        ],
      },
    ],
  },
  {
    heading: "Faith & spirituality",
    items: [
      {
        q: "Is faith or spirituality part of your approach?",
        a: [
          "Yes\u2014spiritual principles are a foundational part of how I practice hypnotherapy. Many of my clients are people of faith, and their beliefs and values are honored throughout our work together. While I do not impose any specific religious views, I often integrate spiritual discernment, prayerful language, and values-based healing when requested or appropriate.",
          "This approach helps clients not only heal emotionally, but also realign spiritually, restore their sense of identity, and reconnect with what matters most to them. For clients who prefer a non-spiritual framework, the work remains fully effective and deeply respectful.",
        ],
      },
      {
        q: "Is hypnotherapy compatible with my faith?",
        a: [
          "Yes. Hypnotherapy is not a religion or belief system\u2014it's a method of helping the mind heal and change. For clients who are spiritually inclined, including many members of The Church of Jesus Christ of Latter-day Saints, I often integrate prayerful language, spiritual discernment, and values-based principles into our sessions. The process is meant to support your faith\u2014not replace it.",
        ],
      },
      {
        q: "Do you ever address spiritual or adversarial influences in a session?",
        a: [
          "Yes, when appropriate and with your consent. Some clients experience intrusive thoughts, emotional oppression, or spiritual disconnection they feel may be adversarial in nature. In these cases, I draw on spiritually grounded hypnotherapy approaches\u2014often involving discernment, prayer, and truth-centered reframing\u2014to help clients recognize and release those influences in a safe, faith-aligned way.",
        ],
      },
    ],
  },
  {
    heading: "Working together",
    items: [
      {
        q: "Can my spouse or partner be involved?",
        a: [
          "That depends on the service and your goals.",
          "TENET also provides assessments and educational resources for partners, spouses, and family members affected by another person's behavior.",
          "If relationship repair or couples work is needed, a qualified licensed couples or mental-health professional may also be appropriate.",
        ],
      },
    ],
  },
];

export default function FAQ() {
  return (
    <div className="pb-8" data-testid="faq-page">
      {/* Intro */}
      <Section className="pt-14 sm:pt-20">
        <div className="max-w-3xl">
          <Eyebrow>Frequently asked questions</Eyebrow>
          <h1 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl font-normal tracking-tight text-stone-900 leading-[1.08] mt-5">
            Have questions? You're not alone.
          </h1>
          <div className="mt-6 space-y-4 text-lg text-stone-600 leading-relaxed">
            <p>
              Whether you're new to hypnotherapy or simply curious about what it feels like to begin
              this kind of work, this page offers clear, thoughtful answers to the most common
              questions.
            </p>
            <p>
              Topics include healing trauma, subconscious change, spiritual support, and what
              actually happens during a session.
            </p>
            <p className="text-base text-stone-500">
              Still have questions after reading?{" "}
              <Link to="/consultation" className="text-[#1F3D2E] underline underline-offset-2">
                Feel free to reach out
              </Link>
              {" "}&mdash; guidance and support are always available.
            </p>
          </div>
        </div>
      </Section>

      {/* Where should I start? */}
      <Section className="mt-14 sm:mt-20">
        <div className="max-w-2xl">
          <Eyebrow>Where should I start?</Eyebrow>
          <h2 className="font-playfair text-3xl text-stone-900 mt-3">
            Three gentle ways to begin
          </h2>
        </div>
        <div className="grid md:grid-cols-3 gap-6 mt-8" data-testid="faq-start-cards">
          {startHere.map((s) => (
            <div
              key={s.title}
              className="bg-white rounded-2xl border border-stone-200 p-7 flex flex-col hover:-translate-y-1 hover:shadow-xl transition-all duration-300"
            >
              <div className="grid place-items-center h-12 w-12 rounded-xl bg-[#1F3D2E]/10">
                <i className={`fa-solid ${s.icon} text-[#1F3D2E] text-lg`} />
              </div>
              <p className="overline-label mt-5">{s.label}</p>
              <h3 className="font-playfair text-xl text-stone-900 mt-2 leading-snug">{s.title}</h3>
              <p className="text-stone-600 text-sm mt-2 leading-relaxed flex-1">{s.body}</p>
              <Link
                to={s.to}
                data-testid={s.testid}
                className="inline-flex items-center gap-2 text-sm font-semibold text-[#1F3D2E] mt-5 hover:gap-3 transition-all"
              >
                {s.cta} <i className="fa-solid fa-arrow-right text-xs" />
              </Link>
            </div>
          ))}
        </div>
      </Section>

      {/* FAQ accordions */}
      <Section className="mt-20 sm:mt-28">
        <div className="max-w-3xl mx-auto space-y-14">
          {faqSections.map((section, si) => (
            <div key={section.heading}>
              <h2 className="font-playfair text-2xl text-stone-900 mb-3">{section.heading}</h2>
              <Accordion type="single" collapsible className="w-full">
                {section.items.map((item, ii) => (
                  <AccordionItem
                    key={item.q}
                    value={`s${si}-i${ii}`}
                    data-testid={`faq-item-${si}-${ii}`}
                    className="border-b border-stone-200"
                  >
                    <AccordionTrigger className="text-left font-playfair text-lg text-stone-900 hover:no-underline py-5">
                      {item.q}
                    </AccordionTrigger>
                    <AccordionContent className="text-stone-600 leading-relaxed text-base pb-6">
                      <div className="space-y-3">
                        {item.a.map((p) => (
                          <p key={p}>{p}</p>
                        ))}
                        {item.list && (
                          <ul className="space-y-2 pt-1">
                            {item.list.map((li) => (
                              <li key={li} className="flex items-start gap-2.5">
                                <i className="fa-solid fa-circle-check text-[#8B6F3D] mt-1 text-sm" />
                                <span>{li}</span>
                              </li>
                            ))}
                          </ul>
                        )}
                        {item.after && item.after.map((p) => <p key={p}>{p}</p>)}
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          ))}
        </div>
      </Section>

      {/* Closing CTA */}
      <Section className="mt-20 sm:mt-28">
        <div className="rounded-[2rem] bg-[#1F3D2E] p-10 sm:p-14 text-center">
          <h2 className="font-serif-display text-3xl sm:text-4xl text-amber-50 max-w-2xl mx-auto leading-tight">
            Still have questions?
          </h2>
          <p className="text-stone-300 mt-4 max-w-xl mx-auto leading-relaxed">
            You don't have to have everything figured out before reaching out. A free, no-pressure
            consultation is a gentle first step.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center mt-8">
            <Link
              to="/consultation"
              data-testid="faq-consultation-cta"
              className="rounded-full px-7 py-4 text-sm font-semibold bg-amber-500 text-stone-900 hover:bg-amber-400 transition-colors"
            >
              Schedule a private consultation
            </Link>
            <Link
              to="/assessments/tenet-readiness"
              data-testid="faq-assessment-cta"
              className="rounded-full px-7 py-4 text-sm font-semibold border border-amber-500/40 text-amber-100 hover:bg-amber-500/10 transition-colors"
            >
              Take the Readiness Assessment
            </Link>
          </div>
        </div>
        <DisclaimerNote
          className="mt-8 max-w-3xl"
          text="TENET is not addiction, trauma, or mental-health treatment. Hypnotherapy is offered as a supportive tool that may help and is meant to complement \u2014 not replace \u2014 appropriate professional care. Individual experiences vary, and no specific outcomes are promised."
        />
      </Section>
    </div>
  );
}
